import { QueryClient } from '@tanstack/react-query';
import {
  BookmarkCollectionResponse,
  BookmarkResponse,
  BookmarkContentResponse,
  BookmarkAction,
  BookmarkActionsRequest,
  BookmarkSectionActionRequestBody,
  BookmarkSectionActionResponse,
  BookmarkSectionActionSection,
  BookmarkSectionActionTemplate,
  ProfileData,
  HighlightsResponse,
  UserSessionResponse,
} from '../types/api';

const providerQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export class APIError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

// Parses the cumulative body streamed by POST /data/summarize. The endpoint
// emits a single `key_sentences: [...]\n\n` record followed by repeated
// `delta: <text>\n\n` records. The key_sentences payload is JSON (no literal
// newlines), so the first `\n\n` reliably terminates it; everything after is the
// streamed overview. Parsing the full buffer on each chunk keeps it resilient to
// chunk boundaries splitting a record. Mirrors processSummaryData in read.coffee.
function parseSummaryStream(raw: string): { keySentences: string[]; overview: string } {
  const ksPrefix = 'key_sentences: ';
  const deltaPrefix = 'delta: ';

  let keySentences: string[] = [];
  let deltaPortion = raw;

  const ksIdx = raw.indexOf(ksPrefix);
  if (ksIdx !== -1) {
    const afterKs = raw.slice(ksIdx + ksPrefix.length);
    const sepIdx = afterKs.indexOf('\n\n');
    const jsonPart = sepIdx === -1 ? afterKs : afterKs.slice(0, sepIdx);
    try {
      const parsed = JSON.parse(jsonPart.trim());
      if (Array.isArray(parsed)) {
        keySentences = parsed;
      }
    } catch {
      // The key_sentences JSON has not finished streaming yet.
    }
    deltaPortion = sepIdx === -1 ? '' : afterKs.slice(sepIdx + 2);
  }

  let overview = '';
  if (deltaPortion.includes(deltaPrefix)) {
    for (const part of deltaPortion.split(deltaPrefix)) {
      overview += part.replace(/(\n\n)+$/, '');
    }
  }

  return { keySentences, overview };
}

export interface SummarizeCallbacks {
  onKeySentences?: (sentences: string[]) => void;
  onDelta?: (overview: string) => void;
}

export interface SummarizeParams {
  bookmarkId: number | string;
  formKey: string;
  keySentences?: string[];
  text?: string;
}

function previewResponseBody(body: string, maxLength = 120): string {
  const trimmed = body.trim();
  if (!trimmed) {
    return '(empty body)';
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}...`;
}

async function parseJsonResponse<T>(
  response: Response,
  url: string,
  body: string,
): Promise<T> {
  try {
    return JSON.parse(body) as T;
  } catch {
    throw new APIError(
      `Invalid JSON from ${url}: ${previewResponseBody(body)}`,
      response.status,
    );
  }
}

// Base "Internal API" (ie: ^/data/*) request function for data endpoints
async function fetchFromData<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = endpoint.startsWith('/') ? `/data${endpoint}` : `/data/${endpoint}`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  const body = await response.text();

  if (!response.ok) {
    const errorData = await parseJsonResponse<Record<string, string>>(
      response,
      url,
      body,
    ).catch(() => null);
    throw new APIError(
      errorData?.message || `API error: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  return parseJsonResponse<T>(response, url, body);
}

// based on handlers available in DataHandlers.py
const client = {
  userSession: () => {
    return fetchFromData<UserSessionResponse>('/user_session');
  },

  bookmark: (bookmarkId: number | string) => {
    return fetchFromData<BookmarkResponse>(`/bookmarks/${bookmarkId}`);
  },

  bookmarkActions: (actions: BookmarkAction[]) => {
    const request: BookmarkActionsRequest = { actions };
    return fetchFromData(`/bookmarks/actions`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  bookmarkSectionAction: (
    section: BookmarkSectionActionSection,
    action: BookmarkSectionActionTemplate,
  ) => {
    const body: BookmarkSectionActionRequestBody = { section, action };
    return fetchFromData<BookmarkSectionActionResponse>(`/sections/actions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  bookmarkContent: (bookmarkId: number | string) => {
    return fetchFromData<BookmarkContentResponse>(`/bookmarks/${bookmarkId}/content`);
  },

  bookmarks: (params: {
    section_type: 'home' | 'liked' | 'archive' | 'folder' | 'tag' | 'videos';
    page?: number;
    folder_id?: string;
    tag?: string;
    include_user?: boolean;
    sort?: 'newest' | 'oldest';
  }) => {
    const searchParams = new URLSearchParams();

    // Add required section_type
    searchParams.append('section_type', params.section_type);

    // Add optional parameters
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.folder_id) searchParams.append('folder_id', params.folder_id);
    if (params.tag) searchParams.append('tag', params.tag);
    if (params.include_user) searchParams.append('include_user', 'true');
    if (params.sort) searchParams.append('sort', params.sort);

    return fetchFromData<BookmarkCollectionResponse>(
      `/bookmarks?${searchParams.toString()}`,
    );
  },

  createHighlight: (
    bookmarkId: number | string,
    params: {
      text: string;
      position?: number;
      note?: string;
    },
  ) => {
    const formData = new URLSearchParams();
    formData.append('text', params.text);
    if (params.position !== undefined)
      formData.append('position', params.position.toString());
    if (params.note) formData.append('note', params.note);

    return fetch(`/data/bookmark/${bookmarkId}/highlight`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  deleteHighlight: (highlightId: number | string, formKey?: string) => {
    const formData = new URLSearchParams();
    if (formKey) {
      formData.append('form_key', formKey);
    }

    return fetch(`/data/highlight/${highlightId}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  keySentences: (bookmarkId: number | string, text: string) => {
    const formData = new URLSearchParams();
    formData.append('text', text);

    return fetch(`/data/bookmarks/${bookmarkId}/key_sentences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  progress: (bookmarkId: number | string, progress: number) => {
    const params = new URLSearchParams();
    params.append('progress', `${progress}`);
    params.append('ajax', '1');

    return fetch(`/data/bookmarks/${bookmarkId}/progress?${params.toString()}`, {
      method: 'GET',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    });
  },

  updateHighlightNote: (highlightId: number | string, note: string, formKey: string) => {
    const formData = new URLSearchParams();
    formData.append('note', note);
    formData.append('form_key', formKey);

    return fetch(`/data/highlight/${highlightId}/update_note`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // The server returns an empty response for successful updates
      // Check if there's any content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        // Return a success object for empty responses
        return { success: true };
      }
    });
  },

  addBookmark: async (url: string) => {
    const response = await fetch('/data/bookmarks/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ url }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      throw new Error(data.error || 'Failed to add bookmark');
    }

    return data.bookmark;
  },

  profile: async (userId: string, page = 1) => {
    const search = new URLSearchParams({ page: `${page}` });
    const response = await fetch(`/data/profile/${userId}?${search}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found or profile is private');
      }
      throw new Error(`Failed to fetch profile: ${response.status}`);
    }
    const data = (await response.json()) as ProfileData;
    return data;
  },

  updateBookmark: (
    bookmarkId: number | string,
    params: {
      title: string;
      url: string;
      description?: string;
    },
  ) => {
    return fetchFromData(`/bookmarks/${bookmarkId}/edit`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  highlights: (
    params: {
      page?: number;
      include_user?: boolean;
    } = {},
  ) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.include_user) searchParams.append('include_user', 'true');

    return fetchFromData<HighlightsResponse>(`/notes?${searchParams.toString()}`);
  },

  // Generates an article summary via the streaming POST /data/summarize endpoint.
  // Sends pre-computed key_sentences when available (Premium "summary highlights"
  // path) to avoid recomputation, otherwise sends the article text. Invokes the
  // callbacks progressively as the overview streams in, and resolves with the
  // final overview + key sentences. Throws an APIError carrying the HTTP status
  // (e.g. 412 = Premium required, 429 = rate limited, 422 = text too short).
  summarize: async (
    params: SummarizeParams,
    callbacks: SummarizeCallbacks = {},
    signal?: AbortSignal,
  ): Promise<{ overview: string; keySentences: string[] }> => {
    const body = new URLSearchParams();
    body.append('form_key', params.formKey);
    body.append('bookmark_id', String(params.bookmarkId));
    if (params.keySentences && params.keySentences.length > 0) {
      body.append('key_sentences', JSON.stringify(params.keySentences));
    } else if (params.text) {
      body.append('text', params.text);
    }

    const response = await fetch('/data/summarize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: body.toString(),
      signal,
    });

    if (!response.ok) {
      throw new APIError(`Summarize failed: ${response.status}`, response.status);
    }

    let raw = '';
    let lastKeySentencesLen = 0;

    const emit = () => {
      const parsed = parseSummaryStream(raw);
      if (parsed.keySentences.length !== lastKeySentencesLen) {
        lastKeySentencesLen = parsed.keySentences.length;
        callbacks.onKeySentences?.(parsed.keySentences);
      }
      callbacks.onDelta?.(parsed.overview);
      return parsed;
    };

    const reader = response.body?.getReader();
    if (reader) {
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
        emit();
      }
      raw += decoder.decode();
    } else {
      // Environments without a streaming body (e.g. some test mocks): read once.
      raw = await response.text();
    }

    return emit();
  },
};

export { client, fetchFromData, providerQueryClient };
